<template>
  <div
    class="data-table-layout"
    :class="[
      $attrs.class as string,
      {
        'data-table--sticky-header': stickyHeader,
        'data-table--fill-height': fillHeight,
      },
    ]"
    :style="$attrs.style as string"
  >
    <div class="data-table-inner page-card" :style="innerStyle">
      <div
        v-if="
          (showColumnSettings && (props.columnConfig ?? []).length > 0) || hasSlot('header-left')
        "
        class="data-table-header"
      >
        <div class="data-table-header-left">
          <slot name="header-left" />
        </div>
        <el-popover
          v-if="showColumnSettings && (props.columnConfig ?? []).length > 0"
          v-model:visible="columnSettingsVisible"
          placement="bottom-end"
          :width="200"
          trigger="click"
          popper-class="column-settings-popper"
        >
          <template #reference>
            <YjButton class="column-settings-btn" plain icon="more">设置显示列</YjButton>
          </template>
          <template #default>
            <div class="column-settings-popover">
              <div class="column-settings-header">
                <span class="column-settings-title">
                  <el-icon><Grid /></el-icon>
                  列展示
                </span>
                <el-button type="primary" link size="small" @click="handleResetColumnConfig"
                  >重置</el-button
                >
              </div>
              <el-divider class="column-settings-divider" />
              <Draggable
                :list="props.columnConfig ?? []"
                item-key="id"
                handle=".drag-handle"
                :animation="150"
                class="column-settings-list"
              >
                <template #item="{ element: col }">
                  <label class="column-settings-item">
                    <el-checkbox v-model="col.visible" :disabled="!!col.fixed" />
                    <span class="column-settings-item-label">{{ col.label }}</span>
                    <span
                      class="fixed-toggle fixed-toggle--left"
                      :class="{ 'is-active': col.fixed === 'left' }"
                      title="固定到左侧"
                      @click.prevent.stop="toggleColumnFixed(col, 'left')"
                    >
                      <svg class="fixed-toggle-icon" viewBox="0 0 1024 1024" aria-hidden="true">
                        <path
                          d="M878.3 392.1 631.9 145.7c-6.5-6.5-15-9.7-23.5-9.7s-17 3.2-23.5 9.7L423.8 306.9c-12.2-1.4-24.5-2-36.8-2-73.2 0-146.4 24.1-206.5 72.3a33.23 33.23 0 0 0-2.7 49.4l181.7 181.7-215.4 215.2a15.8 15.8 0 0 0-4.6 9.8l-3.4 37.2c-.9 9.4 6.6 17.4 15.9 17.4.5 0 1 0 1.5-.1l37.2-3.4c3.7-.3 7.2-2 9.8-4.6l215.4-215.4 181.7 181.7c6.5 6.5 15 9.7 23.5 9.7 9.7 0 19.3-4.2 25.9-12.4 56.3-70.3 79.7-158.3 70.2-243.4l161.1-161.1c12.9-12.8 12.9-33.8 0-46.6z"
                        />
                      </svg>
                    </span>
                    <span
                      class="fixed-toggle fixed-toggle--right"
                      :class="{ 'is-active': col.fixed === 'right' }"
                      title="固定到右侧"
                      @click.prevent.stop="toggleColumnFixed(col, 'right')"
                    >
                      <svg class="fixed-toggle-icon" viewBox="0 0 1024 1024" aria-hidden="true">
                        <path
                          d="M878.3 392.1 631.9 145.7c-6.5-6.5-15-9.7-23.5-9.7s-17 3.2-23.5 9.7L423.8 306.9c-12.2-1.4-24.5-2-36.8-2-73.2 0-146.4 24.1-206.5 72.3a33.23 33.23 0 0 0-2.7 49.4l181.7 181.7-215.4 215.2a15.8 15.8 0 0 0-4.6 9.8l-3.4 37.2c-.9 9.4 6.6 17.4 15.9 17.4.5 0 1 0 1.5-.1l37.2-3.4c3.7-.3 7.2-2 9.8-4.6l215.4-215.4 181.7 181.7c6.5 6.5 15 9.7 23.5 9.7 9.7 0 19.3-4.2 25.9-12.4 56.3-70.3 79.7-158.3 70.2-243.4l161.1-161.1c12.9-12.8 12.9-33.8 0-46.6z"
                        />
                      </svg>
                    </span>
                    <span class="drag-handle" title="拖拽排序">
                      <el-icon><Rank /></el-icon>
                    </span>
                  </label>
                </template>
              </Draggable>
              <el-divider class="column-settings-divider" />
              <div class="column-settings-footer flex justify-end">
                <el-button size="small" @click="columnSettingsVisible = false">取消</el-button>
                <el-button type="primary" size="small" @click="handleConfirmColumnConfig">
                  确认
                </el-button>
              </div>
            </div>
          </template>
        </el-popover>
      </div>
      <div class="data-table-body" :style="bodyStyle">
        <el-table
          ref="elTableRef"
          v-loading="loading"
          :data="data"
          row-key="id"
          class="data-table"
          :class="{
            'data-table--row-selectable': showSelection,
            'data-table--resizable': resizableColumns,
          }"
          :border="tableBorder"
          :allow-drag-last-column="!hasActionColumn"
          style="width: 100%"
          v-bind="$attrs"
          @row-click="handleRowClick"
        >
          <el-table-column v-if="showSelection" type="selection" width="55" />
          <el-table-column v-if="showIndex" type="index" label="序号" width="60" align="center" />
          <template v-if="visibleColumns.length > 0">
            <el-table-column
              v-for="col in visibleColumns"
              :key="col.id"
              :prop="col.prop"
              :label="col.label"
              :width="col.width"
              :min-width="col.minWidth ?? 120"
              :align="getColumnAlign(col)"
              :fixed="col.fixed"
              :show-overflow-tooltip="col.showOverflowTooltip"
              :sortable="sortable ? 'custom' : false"
            >
              <template v-if="col.headerTip" #header>
                <span class="dt-header-with-tip">
                  {{ col.label }}
                  <HintIcon :content="col.headerTip" />
                </span>
              </template>
              <template v-if="hasSlot(`col-${col.id}`)" #default="scope">
                <slot :name="`col-${col.id}`" v-bind="scope" />
              </template>
              <template v-else-if="col.formatter" #default="{ row }">
                <el-tag v-if="col.cellType === 'tag'" size="small">{{
                  formatCell(col, row)
                }}</el-tag>
                <span v-else>{{ formatCell(col, row) }}</span>
              </template>
            </el-table-column>
          </template>
          <slot v-if="visibleColumns.length === 0" />
          <el-table-column
            v-if="actionColumn?.buttons?.length"
            :label="actionColumn.label ?? '操作'"
            :width="actionColumnWidth"
            fixed="right"
          >
            <template #default="{ row }">
              <div class="action-column-buttons">
                <template v-for="(btn, idx) in actionColumn.buttons" :key="idx">
                  <span
                    v-if="
                      isActionVisible(btn, row) &&
                      actionColumn.buttons
                        .slice(0, idx)
                        .some((b: ActionColumnConfig['buttons'][number]) => isActionVisible(b, row))
                    "
                    class="action-divider"
                  />
                  <el-button
                    v-if="isActionVisible(btn, row)"
                    :type="btn.type ?? (btn.label === '删除' ? 'danger' : 'primary')"
                    link
                    :size="btn.size"
                    :disabled="isActionDisabled(btn, row)"
                    @click="btn.onClick(row)"
                    >{{ btn.label }}</el-button
                  >
                </template>
                <span v-if="actionColumn.emptyText && !hasVisibleAction(row)">
                  {{ actionColumn.emptyText }}
                </span>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <TablePagination
        v-if="!hidePagination"
        v-model:page="currentPage"
        v-model:page-size="currentPageSize"
        :total="total"
        :page-sizes="pageSizes"
        :show-total="showTotal"
        @page-change="handlePageChange"
        @size-change="handleSizeChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
/** 禁用默认 attr 继承，由内部 el-table 通过 v-bind="$attrs" 接收 row-key、selection-change 等 */
defineOptions({ inheritAttrs: false });

import { Grid, Rank } from '@element-plus/icons-vue';
import Draggable from 'vuedraggable';
import { ref, computed, useSlots } from 'vue';
import type { TableInstance } from 'element-plus';
import type { ColumnConfigItem } from '@/composables/useDataTableLayout';
import type { ActionColumnConfig } from '@/types/actionColumn';
import YjButton from './YjButton.vue';
import TablePagination from './TablePagination.vue';
import HintIcon from './HintIcon.vue';
import { useUserStore } from '@/stores/user';

const slots = useSlots();
const userStore = useUserStore();

type Props = {
  data: unknown[];
  loading?: boolean;
  total: number;
  columnConfig?: ColumnConfigItem[];
  showColumnSettings?: boolean;
  showSelection?: boolean;
  showIndex?: boolean;
  border?: boolean;
  stripe?: boolean;
  minHeight?: string;
  /**
   * 表体最大高度：内容不足时按自然高度，超出后表体自行滚动。
   * 用于「最多看到 N 行」这类诉求，页面整体仍由外层滚动、分页紧跟表格之后。
   */
  maxHeight?: string;
  padding?: string;
  rounded?: string;
  actionColumn?: ActionColumnConfig;
  hidePagination?: boolean;
  page?: number;
  pageSize?: number;
  /** 每页条数可选项，不传用 TablePagination 默认的 [10, 20, 50, 100] */
  pageSizes?: number[];
  /** 是否显示总条数，游标分页无总数时传 false */
  showTotal?: boolean;
  /** 表头是否吸附到滚动容器顶部（默认开），用于长表格滚动时保持列名可见 */
  stickyHeader?: boolean;
  /**
   * 填充高度：撑满父容器剩余空间，表格区自行滚动、分页固定底部。
   * 供 h-full + overflow:hidden 这类受限高度页面使用；默认关闭，其余页面仍按自然高度由外层页面滚动。
   */
  fillHeight?: boolean;
  /**
   * 是否支持拖拽表头调整列宽。
   * el-table 的列宽拖拽依赖 border 能力，开启后内部会同时启用 border 并用样式隐藏竖线，
   * 保持原有「无边框」观感；存在操作列时，操作列仍按自动宽度，不参与拖拽。
   */
  resizableColumns?: boolean;
  /**
   * 配置列表头可排序（sortable="custom"）：组件只抛出 sort-change，
   * 由页面自行排序（如全量数据配合 useClientTableSort）。
   */
  sortable?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  fillHeight: false,
  resizableColumns: false,
  showColumnSettings: true,
  stickyHeader: true,
  // Boolean 类型 prop 不给默认值时 Vue 会 cast 成 false，会让所有表格的总条数消失
  showTotal: true,
});

/** 表体高度约束：min 保底、max 封顶，封顶后表体自身滚动 */
const bodyStyle = computed<Record<string, string>>(() => {
  const style: Record<string, string> = {};
  if (props.minHeight) style.minHeight = props.minHeight;
  if (props.maxHeight) {
    style.maxHeight = props.maxHeight;
    style.overflowY = 'auto';
  }
  return style;
});

const stickyHeader = computed(() => props.stickyHeader);

/** 列宽拖拽必须启用 border，否则 el-table 不响应表头拖拽；视觉上的竖线由样式隐藏 */
const tableBorder = computed(() => props.border || props.resizableColumns);

/** 存在操作列时，最后一列是自动宽度的操作列，不允许拖拽；否则允许拖拽最后一个数据列 */
const hasActionColumn = computed(() => !!props.actionColumn?.buttons?.length);

const showColumnSettings = computed(() => props.showColumnSettings);
const showSelection = computed(() => props.showSelection);
const showIndex = computed(() => props.showIndex);
const hidePagination = computed(() => props.hidePagination);

const emit = defineEmits<{
  'update:page': [value: number];
  'update:pageSize': [value: number];
  pageChange: [page: number];
  sizeChange: [size: number];
  resetColumnConfig: [];
  confirmColumnConfig: [];
}>();

const currentPage = computed({
  get: () => props.page ?? 1,
  set: (v) => emit('update:page', v),
});

const currentPageSize = computed({
  get: () => props.pageSize ?? 20,
  set: (v) => emit('update:pageSize', v),
});

/** 透传到内层 .data-table-inner 的样式：合并 padding 与外部 style 中的圆角等视觉属性 */
const innerStyle = computed(() => {
  const style: Record<string, string> = {};
  if (props.padding !== undefined) style.padding = props.padding;
  if (props.rounded !== undefined) style.borderRadius = props.rounded;
  return style;
});

const columnSettingsVisible = ref(false);

/** 按 visible 过滤，列配置了 btnKey 且无权限的列也不渲染 */
const visibleColumns = computed(() =>
  (props.columnConfig ?? []).filter((c) => {
    if (!c.visible) return false;
    if (c.btnKey && !userStore.hasBtnPermission(c.btnKey)) return false;
    return true;
  }),
);

const hasSlot = (name: string) => {
  return !!slots[name];
};

const formatCell = (col: ColumnConfigItem, row: Record<string, unknown>) => {
  return col.formatter ? col.formatter(row) : '';
};

/** 取列对齐方式，供模板使用避免模板内管道符被误判为 Vue 过滤器 */
function getColumnAlign(col: ColumnConfigItem): 'left' | 'center' | 'right' {
  const a = col.align;
  return a === 'center' || a === 'right' ? a : 'left';
}
/** 切换列固定位置：再次点击同侧取消固定，固定列强制保持显示 */
function toggleColumnFixed(col: ColumnConfigItem, pos: 'left' | 'right') {
  col.fixed = col.fixed === pos ? undefined : pos;
  if (col.fixed) col.visible = true;
}

const handleResetColumnConfig = () => {
  emit('resetColumnConfig');
};

const handleConfirmColumnConfig = () => {
  emit('confirmColumnConfig');
  columnSettingsVisible.value = false;
};

const handlePageChange = (p: number) => {
  emit('pageChange', p);
};

const handleSizeChange = (size: number) => {
  emit('sizeChange', size);
};

const isActionVisible = (
  btn: {
    visible?: boolean | ((row: Record<string, unknown>) => boolean);
    btnKey?: string;
  },
  row: Record<string, unknown>,
): boolean => {
  if (btn.btnKey && !userStore.hasBtnPermission(btn.btnKey)) return false;
  if (btn.visible === undefined) return true;
  return typeof btn.visible === 'function' ? btn.visible(row) : btn.visible;
};

/** 当前行是否有任意操作按钮可见，用于渲染 emptyText 占位 */
const hasVisibleAction = (row: Record<string, unknown>): boolean =>
  (props.actionColumn?.buttons ?? []).some((btn) => isActionVisible(btn, row));

/** 计算按钮文字像素宽度：14px Source Han Sans CN 下中文约15px，英文/数字约8px */
function calcLabelWidth(label: string): number {
  let w = 0;
  for (const ch of label) {
    w += ch.charCodeAt(0) > 127 ? 15 : 8;
  }
  return w;
}

/** 操作列自动宽度：根据每行可见按钮的实际文字宽度计算 */
const actionColumnWidth = computed(() => {
  const ac = props.actionColumn;
  if (!ac?.buttons?.length) return 0;
  if (ac.width !== undefined) return ac.width;

  const BTN_PADDING = 4; // el-button link 左右内边距 2px*2
  const DIVIDER_WIDTH = 9; // 分隔线 1px + margin 4px*2
  const CELL_PADDING = 24; // 单元格 padding 0 12px

  let maxWidth = 0;
  for (const row of props.data as Record<string, unknown>[]) {
    let rowWidth = 0;
    let visibleCount = 0;
    for (const btn of ac.buttons) {
      if (isActionVisible(btn, row)) {
        rowWidth += calcLabelWidth(btn.label) + BTN_PADDING;
        visibleCount++;
      }
    }
    if (visibleCount > 1) rowWidth += (visibleCount - 1) * DIVIDER_WIDTH;
    if (rowWidth > maxWidth) maxWidth = rowWidth;
  }
  // 兜底：数据为空时按全部按钮算
  if (maxWidth === 0 && props.data.length === 0) {
    for (const btn of ac.buttons) maxWidth += calcLabelWidth(btn.label) + BTN_PADDING;
    if (ac.buttons.length > 1) maxWidth += (ac.buttons.length - 1) * DIVIDER_WIDTH;
  }
  // 保证列宽至少能容纳表头文字（如“操作”），避免整页行均无可见按钮时列宽塌缩导致表头竖排
  const headerWidth = calcLabelWidth(ac.label ?? '操作');
  return Math.max(maxWidth, headerWidth) + CELL_PADDING;
});

const isActionDisabled = (
  btn: { disabled?: boolean | ((row: Record<string, unknown>) => boolean) },
  row: Record<string, unknown>,
): boolean => {
  if (btn.disabled === undefined) return false;
  return typeof btn.disabled === 'function' ? btn.disabled(row) : btn.disabled;
};

const elTableRef = ref<TableInstance>();

/** 点击行切换选中状态：仅在显示复选框时启用，点击操作列按钮/链接/复选框时跳过 */
function handleRowClick(row: Record<string, unknown>, _column: unknown, event: Event) {
  if (!props.showSelection) return;
  const target = event.target as HTMLElement | null;
  if (target?.closest('button, a, input, .el-checkbox, .el-switch, .action-column-buttons')) {
    return;
  }
  elTableRef.value?.toggleRowSelection(row, undefined);
}

defineExpose({ elTableRef });
</script>

<style lang="scss" scoped>
@use '@/styles/mixins' as *;

/** 列头提示：标签 + 图标同行不换行，避免窄列时图标掉到第二行 */
.dt-header-with-tip {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.data-table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  gap: 12px;
}

.data-table-header-left {
  display: flex;
  align-items: center;
  flex: 1;
  gap: 10px;

  /** 功能按钮间距统一 10px，覆盖全局 el-button + el-button 的 20px */
  :deep(.el-button + .el-button) {
    margin-left: 0;
  }
}

.column-settings-btn {
  height: 32px;
  padding: 0 14px;
  border-radius: 6px;
  background: #ecf5ff;
  border: 1px solid var(--el-color-primary);
  color: var(--el-color-primary);
  font-size: 13px;

  &:hover,
  &:focus {
    background: #ecf5ff;
    border-color: var(--el-color-primary);
    color: var(--el-color-primary);
  }
}

.data-table-body {
  width: 100%;
}

/**
 * 填充高度：在 h-full + overflow:hidden 的受限容器里，表格自然高度会连同分页一起被外层裁掉。
 * 开启后整块撑满剩余空间，只让表格区滚动，操作行与分页固定在上下两端。
 */
.data-table--fill-height {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  .data-table-inner {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;

    /* 操作行与分页都不参与伸缩，分页因此稳定在底部 */
    > * {
      flex-shrink: 0;
    }

    > .data-table-body {
      flex: 1 1 0%;
      min-height: 0;
      overflow: auto;
    }
  }
}

/**
 * 表头吸附：让 .el-table__header-wrapper 在最近的滚动祖先（通常是 .page-wrapper）顶部 sticky。
 * el-table 默认 overflow:hidden 会让 sticky 失效，这里把 .el-table / .el-table__inner-wrapper 的
 * overflow 改成 visible；表体内部水平滚动仍由 .el-table__body-wrapper 自己处理。
 * z-index 3 用于盖过固定列的内部层级（EP 2.x 内部约 1~2）。
 */
.data-table--sticky-header {
  :deep(.el-table),
  :deep(.el-table__inner-wrapper) {
    overflow: visible;
  }

  :deep(.el-table__header-wrapper) {
    position: sticky;
    top: 0;
    z-index: 3;
  }
}

.data-table-scroll-wrapper {
  width: 100%;
  min-width: min-content;
}

/** 启用 selection 时行可点选 */
.data-table--row-selectable {
  :deep(.el-table__body-wrapper) .el-table__row {
    cursor: pointer;
  }
}

/** 表格样式：浅灰表头、无 hover、白底、细分隔线 */
.data-table {
  :deep(.el-table__header-wrapper) {
    .el-table__header {
      th {
        background: #fafafa;
        color: #666;
        font-weight: 400;
        font-size: 14px;
        height: 48px;
        box-sizing: border-box;
        cursor: pointer;
        border-bottom: none;

        @include laptop {
          height: 40px;
        }

        @include laptop-sm {
          height: 36px;
        }
      }
    }
  }

  :deep(.el-table__body-wrapper) {
    .el-table__body {
      td {
        color: #333;
        font-weight: 400;
        font-size: 14px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.06);
        .cell {
          line-height: 22px;
        }
      }
    }
  }

  :deep(.el-table) {
    --el-table-border-color: #ebeef5;
    --el-table-header-bg-color: #fafafa;

    .cell {
      line-height: 20px;
    }
  }

  :deep(.el-table::before) {
    height: 0;
  }

  :deep(.el-table__body-wrapper) {
    scrollbar-width: thin;
    scrollbar-color: #c0c4cc #f5f7fa;

    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-track {
      background: #f5f7fa;
    }
    &::-webkit-scrollbar-thumb {
      background: #c0c4cc;
      border-radius: 3px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: #909399;
    }
  }
}

/**
 * 列宽拖拽：border 仅用于开启 el-table 的表头拖拽能力，
 * 这里隐去它带来的竖线与外框，保持原有的无边框观感
 */
.data-table--resizable {
  &::before,
  &::after {
    display: none;
  }

  :deep(.el-table__inner-wrapper)::after {
    display: none;
  }

  :deep(.el-table__cell) {
    border-right: none;
  }

  /** 表头的 cursor: pointer 会盖掉拖拽时 body 上的 col-resize，这里改为继承 */
  :deep(.el-table__header-wrapper .el-table__header th) {
    cursor: unset;
  }
}

.column-settings-popover {
  padding: 4px 0;

  .column-settings-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 4px;
  }

  .column-settings-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    color: #303133;
  }

  .column-settings-divider {
    margin: 8px 0;
  }

  .column-settings-list {
    display: flex;
    flex-direction: column;
    gap: 2px;
    max-height: 320px;
    overflow-y: auto;
  }

  .column-settings-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0px 8px;
    cursor: pointer;
    border-radius: 4px;
    font-size: 13px;
    color: #606266;

    &:hover {
      background: #f5f7fa;
    }

    .column-settings-item-label {
      flex: 1;
    }

    .fixed-toggle {
      display: flex;
      align-items: center;
      cursor: pointer;
      color: #c0c4cc;

      .fixed-toggle-icon {
        width: 14px;
        height: 14px;
        fill: currentColor;
      }

      &--left .fixed-toggle-icon {
        transform: scaleX(-1);
      }

      &:hover {
        color: #909399;
      }

      &.is-active {
        color: var(--el-color-primary);
      }
    }

    .drag-handle {
      cursor: grab;
      color: #c0c4cc;
      display: flex;
      align-items: center;

      &:hover {
        color: #909399;
      }

      &:active {
        cursor: grabbing;
      }
    }
  }
}

.action-column-buttons {
  display: inline-flex;
  align-items: center;
}

.action-divider {
  display: inline-block;
  width: 1px;
  height: 14px;
  background: #1a7cff;
  margin: 0 4px;
  vertical-align: middle;
}
</style>
